import * as React from 'react';
import moment from 'moment';
import { shallowEqual } from 'react-redux';
import { Col, Container, Row, Table, Button } from 'react-bootstrap';

import { CopyButton } from '../../../components/CopyButton';
import { RejectToggle } from '../../../components/RejectToggle';
import { FrequencyCurveChart } from '../../../components/Charts/Osc/FrequencyCurveChart';
import { FrequencyCurveModal } from '../../../components/Charts/Osc/FrequencyCurveModal';
import { OscillometryAcceptabilityCriteriaModal } from '../Modals/OscillometryAcceptabilityCriteriaModal';

import { useAppSelector } from '../../../redux/store';
import { OscillometryMeasurement } from '../../../Types/Common/MeasurementTypes';

const { useState } = React;

interface OscillometryWaveformCardProps {
  waveform: OscillometryMeasurement;
  waveformNum: number;
  bestMeasurements: OscillometryMeasurement | undefined;
  toggleRejected: (waveform: OscillometryMeasurement) => Promise<void>;
  saveAcceptabilityCriteria: (waveform: OscillometryMeasurement, criteria: Partial<Record<string, boolean>>) => Promise<void>;
  isReadOnly: boolean | undefined;
  seriesEditing: boolean | undefined;
}

export const OscillometryWaveformCard: React.FC<OscillometryWaveformCardProps> = ({
  waveform,
  waveformNum,
  bestMeasurements,
  toggleRejected,
  saveAcceptabilityCriteria,
  isReadOnly,
  seriesEditing
}) => {
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const [showModal, setShowModal] = useState(false);
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);


  const handleToggleRejected = async () => {
    if (isReadOnly || isLoading) return;
    setIsLoading(true);
    try {
      await toggleRejected(waveform);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div id="waveformContainer" className="OscillometryWaveformCard">
      {/* Header Row */}
      <Row className="p-2">
        <Col sm={6} md={6}>
          <span className="font-weight-bold">
            {translationText.measurementNumber || 'Measurement'} {waveformNum}: {moment.parseZone(waveform.roundtripTime).format('LT')}
          </span>
        </Col>
        <Col sm={6} md={6} className="d-flex justify-content-center align-items-center">
          <RejectToggle
            rejected={!!waveform.rejected}
            handleChange={handleToggleRejected}
            statsText={translationText}
            isLoading={isLoading}
            disabled={!seriesEditing}
          />
          <CopyButton
            textToCopy={waveform.measurementID}
            tooltipText={`${translationText.copy || 'Copy'} ${translationText.measurement || 'Measurement'} ID`}
            id={`measurement-id-${waveform.measurementID}`}
          />
        </Col>
      </Row>

      {/* Chart */}
      <div
        style={{
          width: '100%',
          padding: '0 8px',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
          }}
          onClick={() => setShowModal(true)}
          title={translationText.clickToEnlarge || 'Click to enlarge'}
        >
          <FrequencyCurveChart
            frequencyCurve={waveform?.frequencyCurve}
            small={true}
            height={250}
            width={280}
          />
        </div>
      </div>

      {/* Measurements Table */}
      <Col className="p-0 pb-2 px-2">
        <Container className='table-stats'>
          <Table size="sm" borderless className="mb-0">
            <thead>
              <tr className='border-bottom'>
                <th style={{ maxWidth: 120 }}><i>{waveform?.gdtSource?.referenceName ?? 'Oostveen 2013'}</i></th>
                <th>{translationText.best || 'Best'}</th>
                <th>{translationText.predicted || 'Predicted'}</th>
                <th>{translationText.percentPredicted || '% Pred'}</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: 12 }}>
              <tr>
                <td>R5 (cmH2O.s/L)</td>
                <td>
                  {waveform.R5?.value?.toFixed(2) ?? '--'}
                </td>
                <td>{waveform?.R5?.reference?.toFixed(2) ?? '--'}</td>
                <td>{waveform?.R5?.percentPredicted?.toFixed(0) ?? '--'}%</td>
              </tr>
              <tr>
                <td>R5-20 (cmH2O.s/L)</td>
                <td>
                  {waveform.R5_20?.value?.toFixed(2) ?? '--'}
                </td>
                <td>{waveform?.R5_20?.reference?.toFixed(2) ?? '--'}</td>
                <td>{waveform?.R5_20?.percentPredicted?.toFixed(0) ?? '--'}%</td>
              </tr>
              <tr>
                <td>AX (cmH2O/L)</td>
                <td>
                  {waveform.AX?.value?.toFixed(2) ?? '--'}
                </td>
                <td>{waveform?.AX?.reference?.toFixed(2) ?? '--'}</td>
                <td>{waveform?.AX?.percentPredicted?.toFixed(0) ?? '--'}%</td>
              </tr>
              <tr>
                <td>VT (L)</td>
                <td>
                  {waveform.VT?.value?.toFixed(2) ?? '--'}
                </td>
                <td>{waveform?.VT?.reference?.toFixed(2) ?? '--'}</td>
                <td>{waveform?.VT?.percentPredicted?.toFixed(0) ?? '--'}%</td>
              </tr>
              <tr>
                <td>COH5</td>
                <td>
                  {waveform.COH5?.value?.toFixed(2) ?? '--'}
                </td>
                <td>{waveform?.COH5?.reference?.toFixed(2) ?? '--'}</td>
                <td>{waveform?.COH5?.percentPredicted?.toFixed(0) ?? '--'}%</td>
              </tr>
              <tr>
                <td>Fres ({waveform?.Fres?.unit})</td>
                <td>
                  {waveform.Fres?.value?.toFixed(2) ?? '--'}
                </td>
                <td>{waveform?.Fres?.reference?.toFixed(2) ?? '--'}</td>
                <td>{waveform?.Fres?.percentPredicted?.toFixed(0) ?? '--'}%</td>
              </tr>
              <tr>
                <td>Respiratory Rate ({waveform?.respiratoryRate?.unit})</td>
                <td>
                  {waveform.respiratoryRate?.value?.toFixed(2) ?? '--'}
                </td>
                <td>{waveform?.respiratoryRate?.reference?.toFixed(2) ?? '--'}</td>
                <td>{waveform?.respiratoryRate?.percentPredicted?.toFixed(0) ?? '--'}%</td>
              </tr>
            </tbody>
          </Table>
        </Container>
      </Col>

      <div className="d-flex justify-content-center mb-2">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setShowCriteriaModal(true)}
        >
          {translationText.reviewCriteria || 'Review Criteria'}
        </Button>
      </div>

      {/* Frequency Curve Modal */}
      <FrequencyCurveModal
        show={showModal}
        onHide={() => setShowModal(false)}
        frequencyCurve={waveform?.frequencyCurve}
        title={`${translationText.measurementNumber || 'Measurement'} ${waveformNum} - ${translationText.frequencyCurve || 'Frequency Curve'}`}
      />

      {/* Acceptability Criteria Modal */}
      <OscillometryAcceptabilityCriteriaModal
        show={showCriteriaModal}
        setShow={setShowCriteriaModal}
        waveform={waveform}
        waveformNum={waveformNum}
        isReadOnly={isReadOnly || !seriesEditing}
        onSave={saveAcceptabilityCriteria}
      />
    </div>
  );
};